import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState, StatusBadge } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { PageSkeleton } from '../../components/PageSkeleton';

const FIXTURE_MODE = import.meta.env.VITE_FIXTURE_MODE === 'true';

export function CommandPage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['command'], queryFn: api.command });

  if (isLoading) return <PageSkeleton blocks={6} />;
  if (isError || !data) {
    return <DataQualityState state="unavailable" title="Command surface unavailable" />;
  }

  const falsifiers = data.falsifiers ?? [];
  const actions = [
    ...(data.actionQueue ?? []),
    ...(data.recommendedAction === 'NO_ACTION'
      ? [{ actionId: 'no-action', action: 'NO_ACTION', rationale: 'No position change warranted at current evidence vintage.', priority: 'low' as const }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Command" subtitle="Map the game before the move." />
      <ExplainActions screenName="Command" />

      <section className="regime-field rounded-[var(--radius-canvas)] border border-[var(--color-border-default)] p-6 md:p-8">
        <p className="text-xs uppercase tracking-widest text-[var(--color-atlas-gold)]">Structural sentence</p>
        <p className="mt-3 max-w-4xl text-2xl font-medium leading-snug text-[var(--color-text-primary)] md:text-3xl">
          {data.structuralSentence}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <StatusBadge status="info">{data.regime}</StatusBadge>
          <StatusBadge status={FIXTURE_MODE ? 'partial' : 'fresh'}>
            {FIXTURE_MODE ? 'Fixture mode' : 'Live data'}
          </StatusBadge>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-3">
        {data.powerShifts.map((shift, i) => (
          <AnalyticalPanel key={i} title={`Power shift ${i + 1}`} tone="recessed">
            <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{shift}</p>
          </AnalyticalPanel>
        ))}
      </div>

      <AnalyticalPanel title="Horizon strip" subtitle="Six synchronized market panels">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.panels.map((panel) => (
            <div
              key={panel.panelId}
              className="rounded-[var(--radius-panel)] border border-[var(--color-border-default)] bg-[var(--color-surface-1)] p-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-[var(--color-text-muted)]">{panel.panelId}</span>
                <StatusBadge status={panel.status === 'complete' ? 'fresh' : 'partial'}>
                  {panel.status}
                </StatusBadge>
              </div>
              <p className="mt-2 font-medium text-[var(--color-text-primary)]">{panel.headline}</p>
              <p className="mt-1 font-mono text-sm text-[var(--color-signal-cyan)]">
                {panel.keyMetric}: {panel.keyValue}
              </p>
            </div>
          ))}
        </div>
      </AnalyticalPanel>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <AnalyticalPanel title="Decision runway" subtitle="Including explicit no-action">
          <ul className="space-y-2 text-sm">
            {actions.map((a) => (
              <li
                key={a.actionId}
                className="rounded-[var(--radius-control)] border border-[var(--color-border-default)] p-3"
              >
                <strong
                  className={
                    a.action === 'NO_ACTION'
                      ? 'text-[var(--color-text-muted)]'
                      : 'text-[var(--color-atlas-gold)]'
                  }
                >
                  {a.action}
                </strong>
                <p className="mt-1 text-[var(--color-text-muted)]">{a.rationale}</p>
              </li>
            ))}
          </ul>
        </AnalyticalPanel>
        <div className="space-y-4">
          <AnalyticalPanel title="Falsifiers">
            <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--color-text-secondary)]">
              {falsifiers.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </AnalyticalPanel>
          <AnalyticalPanel title="Confidence" eyebrow="Data coverage">
            <p className="font-mono text-2xl text-[var(--color-signal-cyan)]">
              {(data.sourceCoverage * 100).toFixed(0)}%
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              Freshness: {Object.entries(data.freshness).map(([k, v]) => `${k} ${v}`).join(' · ')}
            </p>
          </AnalyticalPanel>
        </div>
      </div>
    </div>
  );
}
